import { useEffect, useState } from 'react'
import { Spin } from 'antd'
import { useTranslation } from 'react-i18next'
import { LangText } from '@/components'
import { handleClick } from '@/components/OutLink'
import { JAPAN_MALL_URL } from '@/constants'
import { getLangExtField } from '@/constants/appLocale'
import {
  apiHanedaClassifications,
  apiHanedaGoodsPage,
  type HanedaClassification,
  type HanedaGoods,
  type HanedaTerminalLabel,
} from '@/service/api'
import { PlatformEnum } from '@/service/api/types'
import emptyGoodsImg from '@/assets/svg/empty-goods.svg'

const MAX_VISIBLE_PRODUCTS = 8

type ProductData = {
  id: number
  name: string
  langExt?: string
  price: number
  originalPrice: number
  image: string
  terminalLabels: HanedaTerminalLabel[]
}

const getClassificationName = (classification: HanedaClassification, language: string) =>
  getLangExtField(classification.langExt, language, 'name', classification.name)

const getTerminalLabelName = (label: HanedaTerminalLabel, language: string) =>
  getLangExtField(label.langExt, language, 'name', label.name)

const mapGoodsToProduct = (goods: HanedaGoods): ProductData => ({
  id: goods.spuId,
  name: goods.name,
  langExt: goods.langExt,
  price: goods.price ?? 0,
  originalPrice: goods.marketPrice ?? goods.price ?? 0,
  image: goods.fullImgUrl || goods.imgUrl || emptyGoodsImg,
  terminalLabels: goods.terminalLabels ?? [],
})

const formatYen = (value: number) => `¥${value.toLocaleString()}`

const ProductCard = ({ product }: { product: ProductData }) => {
  const { i18n } = useTranslation()
  const saving = Math.max(0, product.originalPrice - product.price)
  const name = getLangExtField(product.langExt, i18n.language, 'name', product.name)

  return (
    <article className="group overflow-hidden rounded-[20px] border border-[#E3DCDD] bg-white">
      <div className="flex h-[240px] items-center justify-center overflow-hidden">
        <img
          src={product.image}
          alt={name}
          className="object-cover w-full h-full transition-transform duration-300 ease-out group-hover:scale-110"
          draggable={false}
        />
      </div>

      <div className="flex flex-col p-[24px]">
        {product.terminalLabels.length > 0 && (
          <div className="flex flex-wrap items-center gap-[10px]">
            {product.terminalLabels.map((label, index) => (
              <span
                key={`${label.name}-${index}`}
                className="rounded-full border border-[#328BF8]/20 bg-[#E6F5FF] text-primary px-[8px] text-[12px] leading-[22px]"
              >
                {getTerminalLabelName(label, i18n.language)}
              </span>
            ))}
          </div>
        )}

        <h3 className="mt-[16px] line-clamp-2 min-h-[56px] text-[20px] font-medium leading-[28px] text-[#000]">
          {name}
        </h3>

        <div className="mt-[12px] flex items-end gap-[8px]">
          <span className="text-[18px] font-bold leading-[26px] text-[#FB5C00]">
            {formatYen(product.price)}
          </span>
        </div>
      </div>
    </article>
  )
}

const ProductShowcase = () => {
  const { i18n } = useTranslation()
  const [classifications, setClassifications] = useState<HanedaClassification[]>([])
  const [activeClassificationId, setActiveClassificationId] = useState<number | null>(null)
  const [products, setProducts] = useState<ProductData[]>([])
  const [classificationLoading, setClassificationLoading] = useState(true)
  const [productLoading, setProductLoading] = useState(false)

  useEffect(() => {
    let ignore = false

    const loadClassifications = async () => {
      setClassificationLoading(true)
      try {
        const response = await apiHanedaClassifications({
          onlyFirstLevel: true,
          storeId: null,
        })
        if (ignore) return

        const nextClassifications = [...(response.data ?? [])].sort(
          (left, right) => (left.sort ?? 0) - (right.sort ?? 0),
        )
        setClassifications(nextClassifications)
        setActiveClassificationId(nextClassifications[0]?.id ?? null)
      } catch {
        if (ignore) return
        setClassifications([])
        setActiveClassificationId(null)
        setProducts([])
      } finally {
        if (!ignore) setClassificationLoading(false)
      }
    }

    void loadClassifications()

    return () => {
      ignore = true
    }
  }, [])

  useEffect(() => {
    if (activeClassificationId === null) {
      setProducts([])
      return
    }

    let ignore = false

    const loadProducts = async () => {
      setProductLoading(true)
      setProducts([])
      try {
        const response = await apiHanedaGoodsPage({
          classificationId: activeClassificationId,
          current: 1,
          size: MAX_VISIBLE_PRODUCTS,
          keyword: '',
          storeId: null,
        })
        if (ignore) return
        setProducts((response.data?.records ?? []).map(mapGoodsToProduct))
      } catch {
        if (!ignore) setProducts([])
      } finally {
        if (!ignore) setProductLoading(false)
      }
    }

    void loadProducts()

    return () => {
      ignore = true
    }
  }, [activeClassificationId])

  const viewAllProducts = () => {
    return handleClick({
      platform: PlatformEnum.JAPANMALL,
      linUrl: JAPAN_MALL_URL,
    })
  }

  return (
    <section className="w-full bg-[#FAFAFA] pt-[60px]">
      <div className="mx-auto w-[1200px]">
        <div className="flex flex-col items-center">
          <LangText
            lngKey="hanedaShop.products.title"
            className="text-[36px] font-bold leading-[50px] text-[#000]"
          />
          <span className="mt-[20px] h-[4px] w-[104px] rounded-full bg-primary" />
        </div>

        <div className="mt-[48px] flex items-center justify-between">
          <div className="flex items-center gap-[12px]">
          {(classifications || []).map((classification) => {
              const isActive = classification.id === activeClassificationId

              return (
                <button
                  key={classification.id}
                  type="button"
                  onClick={() => setActiveClassificationId(classification.id)}
                  className={`min-w-[100px] h-[40px] rounded-[8px] px-[18px] text-[16px] transition-colors ${
                    isActive
                      ? 'border border-primary bg-secondary text-primary'
                      : 'border border-transparent bg-[#F2F2F2] text-[#979797]'
                  }`}
                >
                  {getClassificationName(classification, i18n.language)}
                </button>
              )
            })}
          </div>

          <button
            type="button"
            onClick={viewAllProducts}
            className="h-[40px] cursor-pointer rounded-full bg-primary px-[24px] text-[16px] text-white transition-opacity hover:opacity-90"
          >
            <LangText lngKey="common.action.viewAll" />
          </button>
        </div>

        {classificationLoading || productLoading ? (
          <div className="mt-[24px] flex h-[360px] items-center justify-center rounded-[16px] bg-white">
            <Spin size="large" />
          </div>
        ) : products.length > 0 ? (
          <div className="mt-[24px] grid grid-cols-4 gap-[24px]">
            {products.map((product) => (
              <ProductCard key={product.id} product={product} />
            ))}
          </div>
        ) : (
          <div className="mt-[24px] flex h-[360px] flex-col items-center justify-center rounded-[16px] bg-white">
            <img src={emptyGoodsImg} alt="" className="h-[160px] w-[160px]" draggable={false} />
            <LangText
              lngKey="hanedaShop.products.empty"
              className="mt-[24px] text-[18px] font-medium text-[#333333]"
            />
          </div>
        )}
      </div>
    </section>
  )
}

export default ProductShowcase
